import axios, { AxiosInstance } from 'axios'
import { useAuthStore } from '../stores/auth'

export type WatchListAlertStatus = 'draft' | 'active'

export interface WatchListAlertCondition {
    field: string
    op: string
    value: string
}

export interface WatchListAlertChannels {
    email?: { enabled: boolean; address: string }
    discord?: { enabled: boolean; webhookUrl: string }
    telegram?: { enabled: boolean; chatId: string }
}

export type AlertConditionMode = 'manual' | 'ai'

export interface WatchListAlertPayload {
    name?: string
    conditions: WatchListAlertCondition[]
    channels: WatchListAlertChannels
    actions?: { addToWatchlist?: boolean }
    status: WatchListAlertStatus
    condition_mode?: AlertConditionMode
    ai_description?: string
}

export interface WatchListAlertRow {
    id: string
    watch_list_id: string
    name: string | null
    conditions: WatchListAlertCondition[]
    channels: WatchListAlertChannels
    actions: { addToWatchlist?: boolean }
    status: WatchListAlertStatus
    condition_mode?: AlertConditionMode
    ai_description?: string | null
    created_at: string
    updated_at: string
}

/** Response from analysis `POST /api/v1/alerts/translate` (port 8410). */
export interface AlertTranslatePreviewResponse {
    ok: boolean
    translation?: {
        conditions: WatchListAlertCondition[]
        channels: WatchListAlertChannels
        actions?: { addToWatchlist?: boolean }
        name?: string | null
    }
    model?: string
    raw?: unknown
}

/** AI Builder spec — the typed column definition produced by Stage 2 of
 *  POST /api/v1/ai-builder/draft. The modal stores this as `pendingDraft`
 *  after the user accepts a draft; on Create, it's forwarded to
 *  POST /api/v1/custom-column as `pending_draft` to bypass formula_generator. */
export type AiBuilderSpecKind =
    | 'expression'
    | 'indicator_spec'
    | 'source_model'
    | 'strategy'
    | 'prompt_runner'

export interface AiBuilderDraftSpec {
    kind: AiBuilderSpecKind
    column_name: string
    column_label: string
    output_type: string
    expression?: string
    indicator_spec?: Record<string, unknown>
    source_model?: {
        model: 'historical_prices' | 'insider_trades' | 'news_agent' | 'filings_agent'
        lookback_days: number
        aggregate?: string | null
    }
    strategy?: {
        entry_logic: Record<string, unknown>
        exit_logic?: Record<string, unknown> | null
    }
    prompt_runner?: {
        rendered_prompt: string
        data_source: 'google' | 'reddit' | 'twitter' | 'sec_filings' | 'custom_url'
    }
}

export interface AiBuilderDraftResponse {
    intent?: string
    goal?: string
    quality_checks?: {
        specificity?: string
        format_consistency?: string
        time_anchoring?: string
    }
    improved_prompt?: string
    rationale?: string
    spec: AiBuilderDraftSpec
    explanation?: string
    _model_used_intent?: string
    _model_used_spec?: string
}

/** Response from analysis `POST /api/v1/formula-analyser` (port 8410).
 *  The Formula tab's "Formula Analyser" button uses this — a two-stage
 *  pipeline that runs the intent/goal agent first and then feeds the
 *  improved prompt into mcp_agent.formula_generator.generate_formula. */
export interface FormulaAnalyserResponse {
    intent?: string
    goal?: string
    quality_checks?: {
        specificity?: string
        format_consistency?: string
        time_anchoring?: string
    }
    improved_prompt?: string
    rationale?: string
    formula: string
    column_name?: string
    column_label?: string
    explanation?: string
    data_type?: string
    requires_fields?: string[]
    model_used?: string
    _model_used_intent?: string
    /** 'frontend_live' or 'background_job' — present when the analyser chose
     *  an indicator_spec path (EMA/SMA) instead of a JS expression. */
    execution_type?: string
    /** Raw indicator_spec when execution_type === 'background_job'. */
    indicator_spec?: Record<string, unknown>
}

// API URL configuration
// In production, nginx reverse proxies /api to backend on localhost:8400
// In development, requests go directly to localhost:8400
const getAPIBaseURL = () => {
    console.log('[API] import.meta.env.VITE_API_URL =', import.meta.env.VITE_API_URL)
    console.log('[API] import.meta.env.MODE =', import.meta.env.MODE)

    // Use env var if set
    if (import.meta.env.VITE_API_URL) {
        console.log('[API] Using VITE_API_URL from env:', import.meta.env.VITE_API_URL)
        return import.meta.env.VITE_API_URL
    }

    // For localhost dev, connect directly to backend
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('[API] Using localhost backend at http://localhost:8400')
        return 'http://localhost:8400'
    }

    // For production, use nginx reverse proxy at /api
    console.log('[API] Using nginx reverse proxy at /api (hostname:', window.location.hostname, ')')
    return '/api'
}

// Analysis Service URL (port 8410)
const getAnalysisBaseURL = () => {
    console.log('[ANALYSIS] import.meta.env.VITE_ANALYSIS_URL =', import.meta.env.VITE_ANALYSIS_URL)

    // Use env var if set
    if (import.meta.env.VITE_ANALYSIS_URL) {
        console.log('[ANALYSIS] Using VITE_ANALYSIS_URL from env:', import.meta.env.VITE_ANALYSIS_URL)
        return import.meta.env.VITE_ANALYSIS_URL
    }

    // For localhost dev, connect directly to analysis service
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('[ANALYSIS] Using localhost analysis service at http://localhost:8410')
        return 'http://localhost:8410'
    }

    // For production, use direct connection to analysis service on port 8410
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
    const analysisURL = `${protocol}://${window.location.hostname}:8410`
    console.log('[ANALYSIS] Using production analysis service at', analysisURL)
    return analysisURL
}

export const API_BASE_URL = getAPIBaseURL()
export const ANALYSIS_BASE_URL = getAnalysisBaseURL()
console.log('[API] Final API_BASE_URL:', API_BASE_URL)
console.log('[ANALYSIS] Final ANALYSIS_BASE_URL:', ANALYSIS_BASE_URL)

/** Default analysis client timeout is 120s; logic/backtest hydration for large tables can exceed that. */
const ANALYSIS_EXTENDED_TIMEOUT_MS = 300_000

/**
 * `GET /api/v1/custom-column-logic` only reads tenant DB metadata — it should return quickly.
 * Using the multi-minute backtest timeout here makes v2 tables look “stuck” when analysis is slow or wedged.
 */
const CUSTOM_COLUMN_LOGIC_TIMEOUT_MS = 45_000

class APIClient {
    private axiosInstance: AxiosInstance
    private analysisInstance: AxiosInstance

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
        })

        this.analysisInstance = axios.create({
            baseURL: ANALYSIS_BASE_URL,
            // Analysis endpoints can involve LLM calls and historical computations.
            // Avoid failing UI requests at 30s for legitimate longer-running operations.
            timeout: 120000,
        })

        // Add interceptor to include JWT token - use a callback to get fresh token each time
        const addAuthHeader = (config: any) => {
            const token = localStorage.getItem('auth_token')
            console.log('[API] Request interceptor - token exists:', !!token, 'url:', config.url)

            // Ensure headers object exists
            if (!config.headers) {
                config.headers = {}
            }

            if (token) {
                config.headers.Authorization = `Bearer ${token}`
                console.log('[API] Added Authorization header:', config.headers.Authorization)
            } else {
                console.log('[API] No token found in localStorage')
            }

            return config
        }

        this.axiosInstance.interceptors.request.use(addAuthHeader)
        this.analysisInstance.interceptors.request.use(addAuthHeader)

        // Handle 401 responses (expired/invalid session — not failed login attempts)
        const handle401 = (error: any) => {
            console.log('[API] Response error:', error.response?.status, error.config?.url)
            const requestUrl = String(error.config?.url || '')
            const isLoginRequest = requestUrl.includes('/api/auth/login')
            if (error.response?.status === 401 && !isLoginRequest) {
                console.log('[API] Got 401, clearing auth')
                localStorage.removeItem('auth_token')
                localStorage.removeItem('user')
                useAuthStore.getState().logout()
                window.location.href = '/login'
            }
            return Promise.reject(error)
        }

        this.axiosInstance.interceptors.response.use(
            (response) => {
                console.log('[API] Response success:', response.config.url, response.status)
                return response
            },
            handle401
        )

        this.analysisInstance.interceptors.response.use(
            (response) => {
                console.log('[ANALYSIS] Response success:', response.config.url, response.status)
                return response
            },
            handle401
        )
    }

    private isAnalysisRoutedPath(url: string) {
        return url.startsWith('/api/chat/conversations') || url.startsWith('/api/ai/agents/query')
    }

    // Authentication
    async login(email: string, password: string) {
        return this.axiosInstance.post('/api/auth/login', { email, password })
    }

    async logout() {
        return this.axiosInstance.post('/api/auth/logout')
    }

    // Stocks
    async getStocks(limit = 50, offset = 0, filters: any = {}) {
        const params: Record<string, any> = { limit, offset }

        if (filters.assetType) params['asset-type'] = filters.assetType
        if (filters.minPrice) params.min_price = filters.minPrice
        if (filters.maxPrice) params.max_price = filters.maxPrice
        if (filters.minChangePct) params.min_change_pct = filters.minChangePct
        if (filters.minPerfPct) params.min_perf_pct = filters.minPerfPct
        if (filters.minMarketCap) params.min_market_cap = filters.minMarketCap
        if (filters.maxMarketCap) params.max_market_cap = filters.maxMarketCap
        if (filters.minPE) params.min_pe_ratio = filters.minPE
        if (filters.maxPE) params.max_pe_ratio = filters.maxPE
        if (filters.minEPSGrowth) params.min_eps_growth = filters.minEPSGrowth
        if (filters.minDividendYield) params.min_dividend_yield = filters.minDividendYield
        if (filters.sector) params.sector = filters.sector
        if (filters.minRevenueGrowth) params.min_revenue_growth = filters.minRevenueGrowth
        if (filters.minPEG) params.max_peg_ratio = filters.minPEG
        if (filters.minROE) params.min_roe = filters.minROE
        if (filters.sortBy) params.sort_by = filters.sortBy

        return this.axiosInstance.get('/api/stocks', {
            params,
            timeout: 60000  // Increase timeout to 60 seconds for large stock queries
        })
    }

    async getStock(ticker: string) {
        return this.axiosInstance.get(`/api/stocks/${ticker}`)
    }

    async getStockAnnualReports(ticker: string) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/annual-reports`)
    }

    async getStockAnnualFinancials(ticker: string, year: number) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/annual-financials/${year}`)
    }

    async getStockEarningsHistory(ticker: string) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/earnings-history`)
    }

    async getMarketEarningsReport(symbol: string, refresh = false) {
        return this.axiosInstance.get(`/api/market/earnings/report/${symbol}`, {
            params: refresh ? { refresh: 'true' } : undefined,
        })
    }

    // Watch Lists
    async createWatchList(name: string, description?: string, isPublic = false, automationPlan?: any, watchListType: 'watchlist' | 'table' = 'watchlist') {
        return this.axiosInstance.post('/api/watch-lists', {
            name,
            description,
            is_public: isPublic,
            automation_plan: automationPlan,
            watch_list_type: watchListType
        })
    }

    async getWatchLists(type?: 'table' | 'watchlist') {
        if (type) {
            return this.axiosInstance.get(`/api/watch-lists/${type}`)
        }
        return this.axiosInstance.get('/api/watch-lists')
    }

    async getWatchList(id: string) {
        return this.axiosInstance.get(`/api/watch-lists/${id}`)
    }

    async updateWatchList(id: string, name?: string, description?: string, isPublic?: boolean, automationPlan?: any) {
        return this.axiosInstance.put(`/api/watch-lists/${id}`, {
            name,
            description,
            is_public: isPublic,
            automation_plan: automationPlan
        })
    }

    async deleteWatchList(id: string) {
        return this.axiosInstance.delete(`/api/watch-lists/${id}`)
    }

    async addStockToWatchList(watchListId: string, ticker: string) {
        return this.axiosInstance.post(`/api/watch-lists/${watchListId}/stocks`, {
            ticker
        })
    }

    async addMultipleStocksToWatchList(watchListId: string, tickers: string[]) {
        return this.axiosInstance.post(`/api/watch-lists/${watchListId}/stocks/bulk`, {
            tickers
        })
    }

    async removeStockFromWatchList(watchListId: string, ticker: string) {
        return this.axiosInstance.delete(
            `/api/watch-lists/${watchListId}/stocks/${ticker}`
        )
    }

    async getWatchListAlerts(watchListId: string) {
        return this.axiosInstance.get<{ alerts: WatchListAlertRow[] }>(`/api/watch-lists/${watchListId}/alerts`)
    }

    /** All alerts across the user’s tables and watch lists (single request). */
    async getAllWatchListAlertsAggregated() {
        return this.axiosInstance.get<{
            alerts: Array<
                WatchListAlertRow & { watch_list_name?: string | null; watch_list_type?: string | null }
            >
        }>(`/api/watch-lists/aggregate/alerts`)
    }

    async createWatchListAlert(watchListId: string, body: WatchListAlertPayload) {
        return this.axiosInstance.post<WatchListAlertRow>(`/api/watch-lists/${watchListId}/alerts`, body)
    }

    async updateWatchListAlert(watchListId: string, alertId: string, body: Partial<WatchListAlertPayload>) {
        return this.axiosInstance.put<WatchListAlertRow>(`/api/watch-lists/${watchListId}/alerts/${alertId}`, body)
    }

    async deleteWatchListAlert(watchListId: string, alertId: string) {
        return this.axiosInstance.delete<{ id: string; status: string }>(
            `/api/watch-lists/${watchListId}/alerts/${alertId}`
        )
    }

    /** Preview NL → structured rules via analysis service (8410). */
    async previewAlertTranslate(description: string, preferredModel: string = 'auto', defaultEmail?: string) {
        return this.analysisInstance.post<AlertTranslatePreviewResponse>('/api/v1/alerts/translate', {
            description,
            default_email: defaultEmail?.trim() || '',
            preferred_model: preferredModel === 'auto' ? 'auto' : preferredModel,
        }, { timeout: 45_000 })
    }

    /**
     * Create/update an AI-mode alert via analysis (8410). Persists translated rules here — do not use Express PUT for AI saves.
     */
    async persistAiWatchListAlert(
        watchListId: string,
        body: {
            alert_id?: string | null
            name?: string
            ai_description: string
            status: WatchListAlertStatus
            conditions?: WatchListAlertCondition[]
            channels?: WatchListAlertChannels
            actions?: { addToWatchlist?: boolean }
            preferred_model?: string
        }
    ) {
        const { alert_id, ...rest } = body
        return this.analysisInstance.post<WatchListAlertRow>(
            `/api/v1/watch-lists/${watchListId}/alerts/ai`,
            {
                ...rest,
                ...(alert_id ? { alert_id } : {}),
            },
            { timeout: 120_000 }
        )
    }

    // Custom Columns
    async getWatchListCustomColumns(watchListId: string) {
        return this.axiosInstance.get(`/api/watch-lists/${watchListId}/custom-columns`)
    }

    async deployWatchListCustomColumns(
        watchListId: string,
        options?: {
            symbols?: string[]
            customColumnIds?: string[]
            forceRecompute?: boolean
        }
    ) {
        const params: Record<string, string> = {}
        if (options?.symbols && options.symbols.length > 0) {
            params.symbols = options.symbols.join(',')
        }
        if (options?.customColumnIds && options.customColumnIds.length > 0) {
            params.custom_column_ids = options.customColumnIds.join(',')
        }
        if (options?.forceRecompute) {
            params.force_recompute = '1'
        }
        return this.axiosInstance.get(`/api/watch-lists/${watchListId}/custom-columns/deploy`, { params })
    }

    async updateCustomColumn(watchListId: string, columnId: string, isActive?: boolean, columnLabel?: string) {
        return this.axiosInstance.put(`/api/watch-lists/${watchListId}/custom-columns/${columnId}`, {
            is_active: isActive,
            column_label: columnLabel
        })
    }

    async deleteCustomColumn(watchListId: string, columnId: string) {
        return this.axiosInstance.delete(`/api/watch-lists/${watchListId}/custom-columns/${columnId}`)
    }

    async getColumnTrail(watchListId: string, columnId: string): Promise<any> {
        return this.axiosInstance.get(`/api/watch-lists/${watchListId}/custom-columns/${columnId}/trail`)
    }

    async getColumnSources(watchListId: string, columnId: string, ticker: string): Promise<any> {
        return this.axiosInstance.get(`/api/watch-lists/${watchListId}/custom-columns/${columnId}/sources/${ticker}`)
    }

    async trackPresetUsage(presetId: string, watchListId?: string) {
        return this.axiosInstance.post('/api/presets/usage/track', {
            preset_id: presetId,
            watch_list_id: watchListId || null,
        })
    }

    async getPresetUsageCounts(presetIds?: string[]) {
        const params = presetIds && presetIds.length > 0
            ? { preset_ids: presetIds.join(',') }
            : {}
        return this.axiosInstance.get('/api/presets/usage/counts', { params })
    }

    async getColumnPresets(tabs?: string[]) {
        const params = tabs && tabs.length > 0 ? { tabs: tabs.join(',') } : {}
        return this.axiosInstance.get('/api/column-presets', { params })
    }

    async addPresetColumn(watchListId: string, presetId: string) {
        return this.axiosInstance.post(`/api/watch-lists/${watchListId}/custom-columns/presets`, { preset_id: presetId })
    }

    async saveFormulaColumn(
        watchListId: string,
        columnLabel: string,
        formula: string,
        explanation?: string,
        editColumnId?: string,
        outputType?: string,
        runSchedule?: string,
        conditionFormula?: string,
        currencyCode?: string,
    ) {
        return this.analysisInstance.post('/api/v1/save-formula-column', {
            watch_list_id: watchListId,
            column_label: columnLabel,
            formula,
            explanation: explanation || '',
            edit_column_id: editColumnId || undefined,
            output_type: outputType || 'number',
            run_schedule: runSchedule || 'one-time',
            condition_formula: conditionFormula || undefined,
            currency_code: currencyCode || undefined,
        })
    }

    async getCustomColumnLogic(watchListId: string, includeInactive = false) {
        return this.analysisInstance.get('/api/v1/custom-column-logic', {
            params: {
                watch_list_id: watchListId,
                include_inactive: includeInactive,
            },
            timeout: CUSTOM_COLUMN_LOGIC_TIMEOUT_MS,
        })
    }

    // Charts
    async getChartData(ticker: string, days = 30) {
        return this.axiosInstance.get(`/api/charts/${ticker}`, {
            params: { days }
        })
    }

    // Account
    async getAccount() {
        return this.axiosInstance.get('/api/account')
    }

    async updateAccount(firstName?: string, lastName?: string) {
        return this.axiosInstance.put('/api/account', {
            first_name: firstName,
            last_name: lastName
        })
    }

    // Analysis Service
    async analyzeStocks(symbols?: string[]) {
        return this.axiosInstance.post('/api/v1/analyze', { symbols })
    }

    async getRecommendations(minScore = 60, maxResults = 10) {
        return this.axiosInstance.get('/api/v1/recommendations', {
            params: {
                min_score: minScore,
                max_results: maxResults
            }
        })
    }

    // Stock Details
    async getStockPriceHistory(ticker: string, days = 365, signal?: AbortSignal) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/price-history`, {
            params: { days },
            signal,
        })
    }

    async getStockBars(ticker: string, range = '1Y', signal?: AbortSignal) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/bars`, {
            params: { range, interval: '1D' },
            signal,
        })
    }

    async getStockBarsBefore(ticker: string, before: number, limit = 500, signal?: AbortSignal) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/bars`, {
            params: { before, limit, interval: '1D' },
            signal,
        })
    }

    async getIntraday15MinLatest(symbols: string[]) {
        const joined = (symbols || []).map(s => String(s || '').trim()).filter(Boolean).join(',')
        return this.axiosInstance.get('/api/market/intraday/15min/latest', {
            params: { symbols: joined }
        })
    }

    async getMarketIndicesLatest(refresh = false) {
        return this.axiosInstance.get('/api/market/indices/latest', {
            params: { refresh }
        })
    }

    async getMostActives(limit = 50, refresh = false) {
        return this.axiosInstance.get('/api/market/most-actives', {
            params: { limit, refresh }
        })
    }

    async getBiggestGainers(limit = 50, refresh = false) {
        return this.axiosInstance.get('/api/market/biggest-gainers', {
            params: { limit, refresh }
        })
    }

    async getBiggestLosers(limit = 50, refresh = false) {
        return this.axiosInstance.get('/api/market/biggest-losers', {
            params: { limit, refresh }
        })
    }

    async getDashboardMarketOverview() {
        return this.axiosInstance.get('/api/dashboard/market-overview')
    }

    async getEarningsCalendar(limit = 100, refresh = false) {
        return this.axiosInstance.get('/api/market/earnings/calendar', {
            params: { limit, refresh }
        })
    }

    async getStockInsiderTrades(ticker: string) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/insider-trades`)
    }

    async getStockNews(ticker: string) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/news`)
    }

    async getStockQuickView(ticker: string, signal?: AbortSignal) {
        return this.axiosInstance.get(`/api/stocks/${ticker}/quick-view`, {
            signal,
        })
    }

    // Search stocks by ticker or name
    async searchStocks(query: string) {
        return this.axiosInstance.get('/api/stocks/search', {
            params: { q: query }
        })
    }

    // Search companies by name
    async searchCompaniesByName(query: string) {
        return this.axiosInstance.get('/api/stocks/search-by-name', {
            params: { query }
        })
    }

    // AI-powered stock search
    async searchWithAI(query: string) {
        return this.analysisInstance.get('/api/v1/search-stocks-ai', {
            params: { query }
        })
    }

    // Parse automation prompt using LLM (Analysis Service on port 8410)
    async parseAutomationPrompt(prompt: string) {
        return this.analysisInstance.post('/api/v1/parse-automation', {
            prompt
        })
    }

    // Seed a watch list (table) by running its saved automation_plan.filters
    // against public.stocks_data. Returns immediately with status='queued';
    // the analysis service does the actual work in a background task.
    async seedTableFromPlan(watchListId: string) {
        return this.analysisInstance.post(`/api/v1/watch-lists/${watchListId}/seed-from-plan`)
    }

    // Poll the current seeding status for a watch list. Returns
    // { seed_status: { state: 'idle'|'queued'|'running'|'completed'|'failed', ... } }
    async getTableSeedStatus(watchListId: string) {
        return this.analysisInstance.get(`/api/v1/watch-lists/${watchListId}/seed-status`)
    }

    // Manually trigger automation execution for a watch list
    async executeAutomation(watchListId: string) {
        return this.axiosInstance.post(`/api/watch-lists/${watchListId}/automation/execute`)
    }

    // User Preferences
    async getUserPreferences() {
        return this.axiosInstance.get('/api/users/preferences')
    }

    async getUserPreference(preferenceKey: string) {
        try {
            const response = await this.axiosInstance.get(`/api/users/preferences/${preferenceKey}`)
            return response.data.value
        } catch (error) {
            console.error(`Failed to get ${preferenceKey} preference:`, error)
            return null
        }
    }

    async saveUserPreference(preferenceKey: string, value: any) {
        return this.axiosInstance.post('/api/users/preferences', {
            preference_key: preferenceKey,
            value: value
        })
    }

    async saveUserPreferences(visibleColumns: string[]) {
        return this.axiosInstance.post('/api/users/preferences', {
            visible_columns: visibleColumns
        })
    }

    async setupWatchListAutomation(watchListId: string, criteria: any) {
        return this.axiosInstance.post(`/api/watch-lists/${watchListId}/automation`, {
            criteria
        })
    }

    // Add missing ticker to database from FMP
    async addTickerToDatabase(ticker: string) {
        return this.axiosInstance.post(`/api/stocks/add-ticker/${ticker}`, {})
    }

    // Refresh FMP data for specific tickers in public.stocks_data
    async refreshTickers(tickers: string[]): Promise<any> {
        return this.analysisInstance.post('/api/v1/refresh-tickers', { tickers })
    }

    // Create custom column with AI
    async createCustomColumnWithAI(
        description: string,
        watchListTickers?: string[],
        watchListId?: string,
        columnName?: string,
        editColumnId?: string,
        preferredModel?: 'openai' | 'gemini' | 'claude',
        strategyConfig?: {
            strategy_mode?: boolean
            strategy_outputs?: string[]
            save_as_template?: boolean
            template_name?: string
            template_kind?: 'strategy' | 'indicator'
            template_id?: string
            template_is_shared?: boolean
            backtesting?: {
                enabled: boolean
                investment_amount?: number
                position_size_mode?: 'pct_equity' | 'fixed_amount'
                position_size_value?: number
                risk_profile?: 'conservative' | 'balanced' | 'aggressive'
                start_date?: string
                end_date?: string
                rebalance_frequency?: 'daily' | 'weekly' | 'monthly'
            }
        },
        outputType?: string,
        dataSource?: string,
        customUrls?: string[],
        runSchedule?: string,
        runMode?: 'first10' | 'all' | 'one' | 'none',
        conditionFormula?: string,
        secFilingType?: string,
        pendingDraft?: AiBuilderDraftSpec | null
    ) {
        // Normalise currency_xxx → currency and extract the code (e.g. currency_usd → USD)
        const rawOutputType = outputType || 'number'
        const isCurrency = rawOutputType.startsWith('currency_')
        const normalizedOutputType = isCurrency ? 'currency' : rawOutputType
        const currencyCode = isCurrency ? rawOutputType.split('_').slice(1).join('_').toUpperCase() : undefined

        const validUrls = (customUrls || []).filter(u => u && u.trim())

        return this.analysisInstance.post(
            '/api/v1/custom-column',
            {
                description,
                user_prompt: description || '',
                column_label: columnName || undefined,
                column_name: columnName || undefined,
                edit_column_id: editColumnId || undefined,
                preferred_model: preferredModel || undefined,
                watch_list_id: watchListId, // New: pass watch list ID for saving
                watch_list_tickers: watchListTickers || [],
                formula_only: false,
                output_type: normalizedOutputType,
                currency_code: currencyCode || undefined,
                run_schedule: runSchedule || 'one-time',
                run_mode: runMode || 'all',
                condition_formula: conditionFormula || undefined,
                data_source: dataSource || 'none',
                sec_filing_type: secFilingType || '10-K',
                custom_url: validUrls[0] || '',
                custom_urls: validUrls,
                strategy_mode: Boolean(strategyConfig?.strategy_mode),
                strategy_outputs: strategyConfig?.strategy_outputs || undefined,
                save_as_template: Boolean(strategyConfig?.save_as_template),
                template_name: strategyConfig?.template_name || undefined,
                template_kind: strategyConfig?.template_kind || undefined,
                template_id: strategyConfig?.template_id || undefined,
                template_is_shared: strategyConfig?.template_is_shared,
                backtesting: strategyConfig?.backtesting || undefined,
                pending_draft: pendingDraft || undefined,
            },
            {
                timeout: 120000,
            }
        )
    }

    /**
     * AI Builder two-stage draft.
     * Stage 1: Intent / Goal agent. Stage 2: typed-spec drafter.
     * Returns { intent, goal, quality_checks, improved_prompt, rationale,
     *           spec: { kind, column_name, column_label, output_type, ... },
     *           explanation }
     *
     * This is the AI Builder's own brain and intentionally does NOT touch
     * mcp_agent.formula_generator on the backend.
     */
    async aiBuilderDraft(
        description: string,
        outputType: string,
        dataSource: string,
        preferredModel?: 'openai' | 'gemini' | 'claude',
        columnName?: string,
    ) {
        return this.analysisInstance.post(
            '/api/v1/ai-builder/draft',
            {
                description,
                column_name: columnName || undefined,
                output_type: outputType || 'text',
                data_source: dataSource || 'none',
                preferred_model: preferredModel || 'openai',
            },
            { timeout: 90000 }
        )
    }

    /**
     * Formula Analyser — Formula tab's "rigorous" generate button.
     * Hits POST /api/v1/formula-analyser on the analysis service (port 8410)
     * which runs the intent agent + mcp_agent.formula_generator pipeline.
     * Returns the formula string plus intent / goal / quality checks /
     * improved prompt / rationale / explanation for the preview UI.
     */
    async analyseFormula(
        description: string,
        outputType?: string,
        preferredModel?: 'openai' | 'gemini' | 'claude',
    ) {
        return this.analysisInstance.post(
            '/api/v1/formula-analyser',
            {
                description,
                output_type: outputType || 'number',
                preferred_model: preferredModel || 'openai',
            },
            { timeout: 90000 }
        )
    }

    // Analyze custom column description without saving a column
    async improveColumnPrompt(description: string, outputType: string, dataSource: string) {
        return this.analysisInstance.post(
            '/api/v1/improve-column-prompt',
            { description, output_type: outputType, data_source: dataSource },
            { timeout: 60000 }
        )
    }

    async analyzeCustomColumnDescription(
        description: string,
        preferredModel?: 'openai' | 'gemini' | 'claude'
    ) {
        return this.analysisInstance.post(
            '/api/v1/custom-column',
            {
                description,
                formula_only: true,
                preferred_model: preferredModel || undefined,
            },
            {
                timeout: 60000,
            }
        )
    }

    // Suggest custom column name from AI Builder description
    async suggestColumnName(description: string) {
        return this.analysisInstance.post('/api/v1/suggest-column-name', {
            description,
        })
    }

    async runCustomColumnBacktest(
        strategyId: string,
        symbols?: string[],
        backtesting?: {
            enabled: boolean
            investment_amount?: number
            position_size_mode?: 'pct_equity' | 'fixed_amount'
            position_size_value?: number
            risk_profile?: 'conservative' | 'balanced' | 'aggressive'
            start_date?: string
            end_date?: string
            rebalance_frequency?: 'daily' | 'weekly' | 'monthly'
        }
    ) {
        return this.analysisInstance.post(
            '/api/v1/custom-column-backtest',
            {
                strategy_id: strategyId,
                symbols: symbols || undefined,
                backtesting: backtesting || undefined,
            },
            { timeout: ANALYSIS_EXTENDED_TIMEOUT_MS }
        )
    }

    async refreshWatchListStrategyBacktests(
        watchListId: string,
        strategyIds?: string[],
        symbols?: string[],
        forceRefresh = false,
        staleAfterMinutes = 15,
    ) {
        return this.analysisInstance.post(
            '/api/v1/custom-column-backtest/refresh-watchlist',
            {
                watch_list_id: watchListId,
                strategy_ids: strategyIds || undefined,
                symbols: symbols || undefined,
                force_refresh: forceRefresh,
                stale_after_minutes: staleAfterMinutes,
            },
            { timeout: ANALYSIS_EXTENDED_TIMEOUT_MS }
        )
    }

    async getStrategyPlotData(strategyId: string, symbol: string, startDate?: string, endDate?: string) {
        return this.analysisInstance.get(`/api/v1/strategies/${strategyId}/stocks/${symbol}/plot-data`, {
            params: {
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            },
        })
    }

    /**
     * Direct access to analysis service (FastAPI, port 8410).
     * Use this when the path is clearly owned by the analysis service.
     */
    async analysisGet(url: string, config: any = {}) {
        return this.analysisInstance.get(url, config)
    }

    async analysisPost(url: string, data: any = {}, config: any = {}) {
        return this.analysisInstance.post(url, data, config)
    }

    async analysisPut(url: string, data: any = {}, config: any = {}) {
        return this.analysisInstance.put(url, data, config)
    }

    async analysisDelete(url: string, config: any = {}) {
        return this.analysisInstance.delete(url, config)
    }

    /**
     * POST /api/ai/agents/query with `stream: true` — returns final JSON body from the SSE `done` event.
     * `onSseEvent` receives parsed `data: {...}` payloads (`start` | `text` | `done` | `error`).
     */
    async postAgentsQuerySse(
        data: Record<string, unknown>,
        opts?: { onSseEvent?: (ev: { type?: string; [k: string]: unknown }) => void }
    ): Promise<unknown> {
        const token = localStorage.getItem('auth_token')
        const agentsQueryUrl = `${ANALYSIS_BASE_URL.replace(/\/$/, '')}/api/ai/agents/query`
        const res = await fetch(agentsQueryUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ ...data, stream: true }),
        })
        if (res.status === 401) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user')
            useAuthStore.getState().logout()
            window.location.href = '/login'
            throw new Error('Unauthorized')
        }
        if (!res.ok) {
            const errText = await res.text().catch(() => '')
            throw new Error(errText || `HTTP ${res.status}`)
        }
        const responseContentType = res.headers.get('content-type') || ''
        if (!responseContentType.includes('text/event-stream')) {
            const text = await res.text()
            if (!text.trim()) {
                throw new Error('Empty agent query response')
            }
            try {
                return JSON.parse(text)
            } catch {
                throw new Error(text)
            }
        }
        const reader = res.body?.getReader()
        if (!reader) {
            throw new Error('No response body')
        }
        const decoder = new TextDecoder()
        let buffer = ''
        let doneData: unknown = null
        const handlePayload = (j: { type?: string; [k: string]: unknown }) => {
            opts?.onSseEvent?.(j)
            if (j.type === 'done' && 'data' in j) {
                doneData = (j as { data: unknown }).data
            }
            if (j.type === 'error') {
                const d = (j as { detail?: string }).detail
                const detail = typeof d === 'string' ? d : 'Agent query error'
                throw new Error(detail)
            }
        }
        for (;;) {
            const { value, done: streamDone } = await reader.read()
            if (value) {
                buffer += decoder.decode(value, { stream: true })
            }
            for (;;) {
                const idx = buffer.indexOf('\n\n')
                if (idx === -1) {
                    break
                }
                const eventBlock = buffer.slice(0, idx)
                buffer = buffer.slice(idx + 2)
                const dataLine = eventBlock
                    .split('\n')
                    .map((l) => l.trimEnd())
                    .find((l) => l.startsWith('data:'))
                if (!dataLine) {
                    continue
                }
                const jsonStr = dataLine.replace(/^data:\s*/i, '').trim()
                if (jsonStr === '[DONE]') {
                    continue
                }
                let j: { type?: string; [k: string]: unknown }
                try {
                    j = JSON.parse(jsonStr) as { type?: string; [k: string]: unknown }
                } catch {
                    continue
                }
                handlePayload(j)
            }
            if (streamDone) {
                break
            }
        }
        if (buffer.trim()) {
            const dataLine = buffer
                .split('\n')
                .map((l) => l.trimEnd())
                .find((l) => l.startsWith('data:'))
            if (dataLine) {
                const jsonStr = dataLine.replace(/^data:\s*/i, '').trim()
                if (jsonStr && jsonStr !== '[DONE]') {
                    let j2: { type?: string; [k: string]: unknown }
                    try {
                        j2 = JSON.parse(jsonStr) as { type?: string; [k: string]: unknown }
                    } catch {
                        // ignore trailing garbage / partial
                        j2 = { type: undefined }
                    }
                    if (j2.type !== undefined) {
                        handlePayload(j2)
                    }
                }
            }
        }
        if (doneData === null) {
            throw new Error('Stream ended without a done event')
        }
        return doneData
    }

    // Generic post method for other requests
    async post(url: string, data: any = {}) {
        if (this.isAnalysisRoutedPath(url)) {
            return this.analysisInstance.post(url, data)
        }
        return this.axiosInstance.post(url, data)
    }

    // Generic get method for other requests
    async get(url: string, config: any = {}) {
        if (this.isAnalysisRoutedPath(url)) {
            return this.analysisInstance.get(url, config)
        }
        return this.axiosInstance.get(url, config)
    }

    // Generic put method for other requests
    async put(url: string, data: any = {}) {
        if (this.isAnalysisRoutedPath(url)) {
            return this.analysisInstance.put(url, data)
        }
        return this.axiosInstance.put(url, data)
    }

    // Generic delete method for other requests
    async delete(url: string) {
        if (this.isAnalysisRoutedPath(url)) {
            return this.analysisInstance.delete(url)
        }
        return this.axiosInstance.delete(url)
    }

    // Generic patch method (mirrors put/post). Added for the Agents CRUD layer
    // which uses PATCH for partial updates; the rest of the app can use it too.
    async patch(url: string, data: any = {}) {
        if (this.isAnalysisRoutedPath(url)) {
            return this.analysisInstance.patch(url, data)
        }
        return this.axiosInstance.patch(url, data)
    }

    // -------------------------------------------------------------------------
    // YouTube Sources
    // -------------------------------------------------------------------------
    async createYouTubeSource(payload: {
        source_url: string
        source_type: 'channel' | 'video'
        display_name?: string
        max_videos?: number
        influencer_id?: string
    }) {
        return this.axiosInstance.post('/api/youtube/sources', payload, { timeout: 30000 })
    }

    async getYouTubeSource(sourceId: string) {
        return this.axiosInstance.get(`/api/youtube/sources/${sourceId}`)
    }

    async refreshYouTubeSource(sourceId: string, maxVideos?: number) {
        return this.axiosInstance.post(
            `/api/youtube/sources/${sourceId}/refresh`,
            { max_videos: maxVideos },
            { timeout: 30000 }
        )
    }

    async getYouTubeSourcePicks(sourceId: string, limit = 50, offset = 0) {
        return this.axiosInstance.get(`/api/youtube/sources/${sourceId}/picks`, {
            params: { limit, offset },
        })
    }
}

export const apiClient = new APIClient()
